import { useQuery } from "@tanstack/react-query";
import api from "../../http";
import { RequestProduct } from "../../../products/types";

interface PaginatedResponse {
    docs: RequestProduct[];
    totalDocs: number;
    limit: number;
    totalPages: number;
    page: number;
    pagingCounter: number;
    hasPrevPage: boolean;
    hasNextPage: boolean;
    prevPage: number | null;
    nextPage: number | null;
}

interface UseFilterRequestProductsParams {
    page?: number;
    limit?: number;
    refetchInterval?: number | false;
    categories?: string[];
    status?: 'PENDING' | 'APPROVED' | 'REJECTED';
    enabled?: boolean;
    period?: 'last15Days' | 'last30Days' | 'custom';
    startDate?: string;
    endDate?: string;
}

export const useFilterRequestProducts = ({ page = 1, limit = 10, refetchInterval = false, categories, status, period, startDate, endDate, enabled = true }: UseFilterRequestProductsParams = {}) => {
    const query = useQuery({
        queryKey: ['request-products', 'filter', page, limit, categories, status, period, startDate, endDate],
        refetchInterval,
        enabled,
        placeholderData: (previousData, previousQuery) =>
            JSON.stringify(previousQuery?.queryKey.slice(4)) === JSON.stringify([categories, status, period, startDate, endDate])
                ? previousData : undefined,
        queryFn: async (): Promise<PaginatedResponse> => {
            const { data } = await api.get<PaginatedResponse>('/request-products/filter', {
                params: { page, limit, categories: categories?.length ? categories.join(',') : undefined, status, period, startDate, endDate }
            });
            return data;
        },
    });

    return {
        ...query,
        data: query.data?.docs ?? [],
        pagination: query.data ? {
            totalDocs: query.data.totalDocs,
            totalPages: query.data.totalPages,
            page: query.data.page,
            hasPrevPage: query.data.hasPrevPage,
            hasNextPage: query.data.hasNextPage,
            prevPage: query.data.prevPage,
            nextPage: query.data.nextPage,
        } : null,
    };
};
